/**
 * 企业微信扫码登录 — 迁移自 jx-portal 首页 WwLogin 实时二维码逻辑
 */

import { nextTick, onMounted, onUnmounted, ref, watch, toValue } from 'vue';

const WW_LOGIN_SDK = 'https://wwcdn.weixin.qq.com/node/wework/wwopen/js/wwLogin-1.2.7.js';
const WW_LOGIN_CONTAINER_ID = 'wx_login_container';

/** 企微后台已登记的回调域名（jx-portal 同款） */
const WECOM_REGISTERED_REDIRECT_URI = 'http://jx.1605ai.com';
/** bridge 页部署在 jx.1605ai.com 后，扫码可跳回设计平台 */
const WECOM_BRIDGE_REDIRECT_URI = 'http://jx.1605ai.com/wecom-callback.html';
/** 线上设计平台入口（bridge 未部署时的最终落点） */
const DESIGN_PLATFORM_ENTRY = 'https://tanhui.1605ai.com/jx-picdesign/index.html';

/** 鲸夕设计平台默认企微应用配置，可通过环境变量覆盖 */
const DEFAULT_WECOM_CONFIG = {
  corpId: 'ww129e1e186af00f38',
  agentId: '1000014',
  redirectUri: WECOM_BRIDGE_REDIRECT_URI,
};

export function buildWeComReturnUrl() {
  if (typeof window === 'undefined') return DESIGN_PLATFORM_ENTRY;

  const { origin, pathname } = window.location;
  if (pathname.endsWith('.html')) {
    return `${origin}${pathname}`.replace(/\/$/, '');
  }

  const base = String(import.meta.env.VITE_APP_BASE || '/').replace(/\/$/, '');
  if (!base || base === '/') {
    return origin.replace(/\/$/, '');
  }
  return `${origin}${base}/index.html`.replace(/([^:]\/)\/+/g, '$1');
}

/** 构建 state：随机串 + 扫码完成后跳回的设计平台地址 */
export function buildWeComOAuthState(returnUrl = buildWeComReturnUrl()) {
  return `${generateState()}|${encodeURIComponent(returnUrl)}`;
}

export function parseWeComOAuthState(state) {
  if (!state) return { nonce: '', returnUrl: DESIGN_PLATFORM_ENTRY };
  const splitAt = state.indexOf('|');
  if (splitAt === -1) return { nonce: state, returnUrl: DESIGN_PLATFORM_ENTRY };
  const nonce = state.slice(0, splitAt);
  const encodedReturn = state.slice(splitAt + 1);
  try {
    return { nonce, returnUrl: decodeURIComponent(encodedReturn) || DESIGN_PLATFORM_ENTRY };
  } catch {
    return { nonce, returnUrl: DESIGN_PLATFORM_ENTRY };
  }
}

export function resolveWeComRedirectUri() {
  const fromEnv = import.meta.env.VITE_WECOM_REDIRECT_URI;
  if (fromEnv) return String(fromEnv).trim();

  // 默认走已登记域名下的 bridge；未部署时可改 env 为 http://jx.1605ai.com
  return WECOM_BRIDGE_REDIRECT_URI;
}

/** 清除企微 OAuth 回调留在地址栏的 query（失败后需清理才能重新出码） */
export function clearWeComOAuthQuery() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  ['code', 'state', 'appid'].forEach((key) => url.searchParams.delete(key));
  const next = `${url.pathname}${url.search}${url.hash}`;
  if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    window.history.replaceState({}, document.title, next);
  }
}

export function hasWeComOAuthCode() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('code');
}

function generateState() {
  return `${Date.now()}${Math.random().toString(36).slice(2)}`;
}

/** 通过 WwLogin href 注入样式，隐藏 iframe 内标题/说明，并为二维码保留安全留白 */
export function buildWwLoginStyleHref(qrcodeSize = 240, safePadding = 16) {
  const viewportSize = qrcodeSize + safePadding * 2;
  const css = [
    '.impowerBox .title { display: none !important; }',
    '.impowerBox .info { display: none !important; }',
    '.impowerBox .status { display: none !important; }',
    '.status_icon { display: none !important; }',
    `.impowerBox .qrcode { display: block; width: ${qrcodeSize}px; height: ${qrcodeSize}px; margin: 0 !important; padding: ${safePadding}px !important; border: none; box-sizing: content-box; background: #ffffff; object-fit: contain; }`,
    `.impowerBox { display: flex; align-items: center; justify-content: center; width: ${viewportSize}px; height: ${viewportSize}px; margin: 0; padding: 0; border: none; box-shadow: none; background: #ffffff; overflow: hidden; }`,
    `html, body { width: ${viewportSize}px; height: ${viewportSize}px; margin: 0; padding: 0; overflow: hidden; background: #ffffff; }`,
  ].join('\n');

  if (typeof btoa === 'undefined') return '';
  return `data:text/css;base64,${btoa(css)}`;
}

export function resolveWeComConfig() {
  const corpId = import.meta.env.VITE_WECOM_CORP_ID || DEFAULT_WECOM_CONFIG.corpId;
  const agentId = import.meta.env.VITE_WECOM_AGENT_ID || DEFAULT_WECOM_CONFIG.agentId;
  if (!corpId || !agentId) return null;
  return { corpId, agentId };
}

function loadWwLoginScript() {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.WwLogin) return Promise.resolve(true);

  return new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${WW_LOGIN_SDK}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(Boolean(window.WwLogin)), { once: true });
      existing.addEventListener('error', () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = WW_LOGIN_SDK;
    script.async = true;
    script.onload = () => resolve(Boolean(window.WwLogin));
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function waitForQrRendered(container, maxAttempts = 80, intervalMs = 100) {
  return new Promise((resolve) => {
    let attempts = 0;
    const tick = () => {
      attempts += 1;
      if (container.querySelector('iframe') || container.querySelector('img')) {
        resolve(true);
        return;
      }
      if (attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      setTimeout(tick, intervalMs);
    };
    setTimeout(tick, intervalMs);
  });
}

/**
 * @param {{ enabled: import('vue').Ref<boolean>|boolean }} options enabled=true 时初始化二维码（如 activeTab==='qr'）
 */
export function useWeComQrLogin(options = {}) {
  const isSdkReady = ref(false);
  const isQrLoading = ref(false);
  const isQrRendered = ref(false);
  const qrError = ref('');

  let initToken = 0;

  async function ensureSdkReady() {
    const ok = await loadWwLoginScript();
    isSdkReady.value = ok;
    if (!ok) {
      qrError.value = '企业微信登录 SDK 加载失败，请检查网络连接';
    }
    return ok;
  }

  async function reinitQrLogin() {
    initToken += 1;
    const container = document.getElementById(WW_LOGIN_CONTAINER_ID);
    if (container) container.innerHTML = '';
    isQrRendered.value = false;
    isQrLoading.value = false;
    qrError.value = '';
    await nextTick();
    await initQrLogin();
  }

  async function initQrLogin() {
    const enabled = options.enabled == null ? true : toValue(options.enabled);
    if (enabled === false) return;

    // 若当前 URL 处于企微授权回调中（带有 ?code=），禁止重新生成 QR 码及覆盖 localStorage 中的 wecom_state！
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has('code')) return;
    }
    if (hasWeComOAuthCode()) return;

    const config = resolveWeComConfig();
    if (!config) {
      qrError.value = '缺少企业微信配置';
      isQrRendered.value = false;
      return;
    }

    if (!isSdkReady.value) {
      const ok = await ensureSdkReady();
      if (!ok) return;
    }

    const container = document.getElementById(WW_LOGIN_CONTAINER_ID);
    if (!container) {
      qrError.value = '二维码容器未就绪，请刷新页面重试';
      isQrLoading.value = false;
      return;
    }
    if (!window.WwLogin) {
      qrError.value = '企业微信登录 SDK 未加载';
      isQrLoading.value = false;
      return;
    }

    const token = ++initToken;
    container.innerHTML = '';
    isQrLoading.value = true;
    isQrRendered.value = false;
    qrError.value = '';

    try {
      const returnUrl = buildWeComReturnUrl();
      const state = buildWeComOAuthState(returnUrl);
      const redirectUri = resolveWeComRedirectUri();
      localStorage.setItem('wecom_state', state);
      localStorage.setItem('wecom_redirect_uri', redirectUri);

      // jx-portal：redirect_uri 传原始 URL，WwLogin SDK 内部自行编码
      new window.WwLogin({
        id: WW_LOGIN_CONTAINER_ID,
        appid: config.corpId,
        agentid: config.agentId,
        redirect_uri: redirectUri,
        state,
        href: buildWwLoginStyleHref(),
        lang: 'zh',
      });

      const rendered = await waitForQrRendered(container);
      if (token !== initToken) return;

      if (rendered) {
        isQrRendered.value = true;
      } else {
        qrError.value = `二维码加载失败，请确认企微后台已登记回调域名「${new URL(redirectUri).host}」，然后刷新重试`;
      }
    } catch (error) {
      if (token !== initToken) return;
      qrError.value = error?.message || '初始化登录组件失败';
      console.warn('[WeComAuth] WwLogin 初始化失败', error);
    } finally {
      if (token === initToken) {
        isQrLoading.value = false;
      }
    }
  }

  onMounted(async () => {
    await ensureSdkReady();
    await nextTick();
    // 带 ?code= 回调时不在此初始化二维码，避免与换 token 流程冲突
    if (!hasWeComOAuthCode()) {
      await initQrLogin();
    }
  });

  if (options.enabled != null) {
    watch(() => toValue(options.enabled), async (active) => {
      if (!active) return;
      await nextTick();
      await initQrLogin();
    });
  }

  onUnmounted(() => {
    initToken += 1;
  });

  return {
    isSdkReady,
    isQrLoading,
    isQrRendered,
    qrError,
    initQrLogin,
    reinitQrLogin,
    resolveWeComConfig,
  };
}
